import { useEffect, useRef } from "react";
import type { SurfDeck } from "@vida/surf-core";
import { resolveSurfPreviewImageUrl } from "@vida/surf-core";
import { createSurfApiConfig, ensureSurfClientConfig, getPdfPresignedUrl } from "../../services/surfClientConfig";
import { downscaleImageBlob, READER_IMAGE_MAX_WIDTH } from "../../utils/downscaleImageBlob";
import { prepareRasterDisplayUrl } from "../../utils/rasterDisplayPrep";
import { assetCacheKey, getCachedAssetUrl } from "../../utils/surfAssetCache";
import { collectSurfDeckAssetItems, surfDeckAssetFingerprint } from "../../utils/surfDeckAssets";

type Options = {
  onProgress?: (done: number, total: number) => void;
  onComplete?: () => void;
};

/** Warm surfAssetCache for all deck raster assets (non-blocking). */
export function useReaderPrefetch(
  deck: SurfDeck | null,
  { onProgress, onComplete }: Options = {},
) {
  const fingerprint = deck ? surfDeckAssetFingerprint(deck) : "";
  const deckRef = useRef(deck);
  deckRef.current = deck;
  const callbacksRef = useRef({ onProgress, onComplete });
  callbacksRef.current = { onProgress, onComplete };

  useEffect(() => {
    const activeDeck = deckRef.current;
    if (!activeDeck || !fingerprint) return;

    let cancelled = false;
    const items = collectSurfDeckAssetItems(activeDeck);
    const previewKey = activeDeck.theme.previewS3Key?.trim();
    const total = items.length + (previewKey ? 1 : 0);
    let done = 0;

    const tick = () => {
      done += 1;
      callbacksRef.current.onProgress?.(done, total);
      if (done >= total) callbacksRef.current.onComplete?.();
    };

    void (async () => {
      try {
        await ensureSurfClientConfig();
        const cfg = createSurfApiConfig();
        const presigned = getPdfPresignedUrl();
        if (!presigned) return;

        await Promise.all(
          items.map(async (item) => {
            if (cancelled) return;
            const cacheKey = assetCacheKey(
              item.key,
              item.chromaKeyPrep ? item.chromaHex : undefined,
              READER_IMAGE_MAX_WIDTH,
            );
            try {
              await getCachedAssetUrl(cacheKey, async () => {
                const presignedUrl = await resolveSurfPreviewImageUrl(
                  presigned,
                  () => cfg.getIdToken(),
                  item.key,
                );
                let blob: Blob;
                if (item.chromaKeyPrep && item.chromaHex) {
                  const prepared = await prepareRasterDisplayUrl(presignedUrl, item.chromaHex);
                  const res = await fetch(prepared, { cache: "no-store" });
                  blob = await res.blob();
                  if (prepared.startsWith("blob:")) URL.revokeObjectURL(prepared);
                } else {
                  const res = await fetch(presignedUrl, { cache: "no-store" });
                  if (!res.ok) throw new Error(`Asset ${res.status}`);
                  blob = await res.blob();
                }
                return downscaleImageBlob(blob, READER_IMAGE_MAX_WIDTH);
              });
            } catch {
              /* best-effort prefetch */
            } finally {
              tick();
            }
          }),
        );

        if (previewKey && !cancelled) {
          try {
            await getCachedAssetUrl(
              assetCacheKey(previewKey, undefined, READER_IMAGE_MAX_WIDTH),
              async () => {
                const presignedUrl = await resolveSurfPreviewImageUrl(
                  presigned,
                  () => cfg.getIdToken(),
                  previewKey,
                );
                const res = await fetch(presignedUrl, { cache: "no-store" });
                if (!res.ok) throw new Error(`Preview ${res.status}`);
                const blob = await res.blob();
                return downscaleImageBlob(blob, READER_IMAGE_MAX_WIDTH);
              },
            );
          } catch {
            /* ignore */
          } finally {
            tick();
          }
        }
      } catch {
        /* offline or config missing */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fingerprint]);
}
